import React from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { cardBorder, darkBlueFontColor } from 'assets/styles/variables'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import { computeGapStrength } from 'features/trend-detection/features/benchmarking-agent/gap-strength-utils'
import { PerformanceBadge } from 'features/trend-detection/features/benchmarking-agent/GapStrengthBadge'
import { SentimentGapBar } from 'features/trend-detection/features/benchmarking-agent/SentimentGapBar'
import { trendDetectionBenchmarkingAgentPath } from 'features/trend-detection/paths'
import type { BenchmarkingInsightFeedItem } from 'features/trend-detection/types'
import { formatUtcDateFromISO } from 'features/trend-detection/utils/helpers'

interface BenchmarkingInsightCardProps {
  insight: BenchmarkingInsightFeedItem
  onClick: (insight: BenchmarkingInsightFeedItem) => void
  projectId: string | null
}

export const BenchmarkingInsightCard: React.FC<
  BenchmarkingInsightCardProps
> = ({ insight, onClick, projectId }) => (
  <InsightCard
    onClick={() => onClick(insight)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick(insight)
      }
    }}
    tabIndex={0}
    role="button"
  >
    <CardInner>
      <CardBody>
        <PerformanceBadge
          strength={computeGapStrength(insight.z_score)}
          direction={
            insight.focus_sentiment != null && insight.cohort_sentiment != null
              ? insight.focus_sentiment >= insight.cohort_sentiment
                ? 'leading'
                : 'lagging'
              : 'leading'
          }
        />
        <InsightTitle>{insight.title ?? 'Benchmarking Insight'}</InsightTitle>
        {insight.summary && <InsightSummary>{insight.summary}</InsightSummary>}
        <CardFooter>
          <FooterText>
            {projectId && insight.agent_uuid ? (
              <AgentLink
                to={trendDetectionBenchmarkingAgentPath(
                  projectId,
                  insight.agent_uuid,
                )}
                onClick={(e) => e.stopPropagation()}
              >
                {insight.agent_name}
              </AgentLink>
            ) : (
              insight.agent_name
            )}{' '}
            ·{' '}
            <Tooltip content="Detection date">
              <span>{formatUtcDateFromISO(insight.create_time)}</span>
            </Tooltip>
          </FooterText>
        </CardFooter>
      </CardBody>
      {insight.focus_sentiment != null &&
        insight.cohort_sentiment != null &&
        insight.focus_entity_name && (
          <SentimentGapBar
            container
            focusSentiment={insight.focus_sentiment}
            cohortSentiment={insight.cohort_sentiment}
            focusEntityName={insight.focus_entity_name}
          />
        )}
    </CardInner>
  </InsightCard>
)

const InsightCard = styled.div`
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  transition:
    box-shadow 0.2s,
    border-color 0.2s;

  &:hover {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
    border-color: ${TREND_DETECTION_PRIMARY_BLUE};
  }

  &:focus {
    outline: 2px solid ${TREND_DETECTION_PRIMARY_BLUE};
    outline-offset: 2px;
  }
`

const CardInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const InsightTitle = styled.h3`
  font-family: Poppins, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: ${darkBlueFontColor};
  margin: 0;
`

const InsightSummary = styled.p`
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: 0;
  color: #868d98;
  margin: 0;
`

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 16px;
`

const FooterText = styled.span`
  font-size: 13px;
  color: #666;
`

const AgentLink = styled(Link)`
  color: ${TREND_DETECTION_PRIMARY_BLUE};
  font-weight: 500;
  text-decoration: none;
  position: relative;
  z-index: 1;

  &:hover {
    text-decoration: underline;
  }
`
